import type { MessageIndex } from "@/scripts/build-message-index"
import {
  resolveCmsProvenance,
  resolveI18nKeyStamp,
  resolveProvenance,
  type CmsProvenance,
  type Provenance,
} from "./provenance"
import { elementText } from "./pick-text-target"

/**
 * What an element resolves to for editing. `null` means "not editable" — the
 * caller must not offer the pencil/outline for it. This is the single source of
 * truth shared by the hover overlay (to gate the affordance) and `onPick` (to
 * open the matching modal), so we never show editing chrome on something that
 * would then dead-end on "couldn't locate this string". (AHD-7410)
 */
export type EditTarget =
  | { kind: "cms"; cms: CmsProvenance }
  | {
      kind: "intl"
      provenance: Extract<Provenance, { status: "exact" | "ambiguous" }>
    }
  | null

/**
 * Resolve an element to an editable target, or `null` when nothing here can be
 * edited. CMS surfaces win (an exact `data-cms-*` stamp), otherwise the visible
 * text must map to a real next-intl key — a `miss` (dynamic / CMS / concatenated
 * text, or an image with no text) is treated as not editable.
 *
 * Note: a string already staged in the cart can have had its DOM text rewritten
 * by preview, so it may resolve to `null` here. The host layers an `elByKey`
 * staged-node check on top of this for that case.
 */
export function resolveEditTarget(
  el: HTMLElement,
  page: string,
  index: MessageIndex | null
): EditTarget {
  const cms = resolveCmsProvenance(el)
  if (cms) return { kind: "cms", cms }
  // Overlap guard: `el` isn't itself inside a CMS surface, but if it *wraps*
  // exactly one CMS-stamped descendant whose visible text is the same, the click
  // belongs to that CMS field — not a coincidentally-matching next-intl string.
  // (e.g. a FAQ accordion button whose text also matches a homepage next-intl
  // key, wrapping the stamped question span.) CMS still wins, like the ancestor
  // case above, and runs before the index check so it resolves without it.
  const nested = resolveNestedCmsProvenance(el)
  if (nested) return { kind: "cms", cms: nested }
  if (!index) return null
  // Exact next-intl key from a render-time `data-i18n-key` stamp wins over the
  // value→key guess below — so a click on a shared-value string (e.g. "Full-body
  // MRI", 12 keys) resolves to the one key that actually rendered here, with no
  // disambiguation radio. Only the un-stamped tail falls through to value lookup.
  const stampKey = resolveI18nKeyStamp(el)
  if (stampKey)
    return { kind: "intl", provenance: { status: "exact", key: stampKey } }
  const text = elementText(el)
  if (!text) return null
  const provenance = resolveProvenance(text, page, index)
  if (provenance.status === "exact" || provenance.status === "ambiguous")
    return { kind: "intl", provenance }
  return null
}

/**
 * Resolve CMS provenance from a stamped *descendant* when `el` wraps exactly one
 * and its visible text is the whole of `el`'s text. Bails on zero or multiple
 * stamps (ambiguous) or any text mismatch (the wrapper carries extra content),
 * so it only fires for the "chrome wraps a single field" case and can never
 * pick the wrong field. (AHD-7396)
 */
function resolveNestedCmsProvenance(el: HTMLElement): CmsProvenance | null {
  const stamped = el.querySelectorAll<HTMLElement>("[data-cms-collection]")
  if (stamped.length !== 1) return null
  const node = stamped[0]
  if (elementText(node) !== elementText(el)) return null
  return resolveCmsProvenance(node)
}
